import { useState } from 'react';
import { Alert, Badge, Button, Input, Select, Textarea } from '@dreamtree-org/twreact-ui';
import { CheckCircle2, Search, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { EmptyState, KeyValue, PageHeader, SectionCard, StatusBadge } from '@/components';
import { useMutation } from '@/lib/useQuery';
import { formatDate } from '@/lib/format';
import { isValidPhone } from '../lib/cart';
import { useStorefront } from '../StorefrontContext';

const PARTS = ['Display', 'Keyboard / trackpad', 'Battery', 'Charging / power', 'Motherboard', 'SSD / storage', 'Ports', 'Other'].map((p) => ({ value: p, label: p }));

/**
 * WEB-10 warranty: lookup by serial (public) or order number (the signed-in buyer),
 * coverage, and — for the buyer — claims and their status (D11).
 */
export default function Warranty() {
  const sf = useStorefront();
  const navigate = useNavigate();
  const lookup = useMutation('sfWarrantyLookup');
  const claim = useMutation('sfWarrantyClaim');
  const [query, setQuery] = useState('');
  const [w, setW] = useState(null);
  const [form, setForm] = useState({ issue: '', description: '', phone: (sf.session && sf.session.phone) || '' });
  const [touched, setTouched] = useState(false);
  const [created, setCreated] = useState(null);

  const find = async (e, value) => {
    if (e) e.preventDefault();
    const v = value || query;
    setCreated(null);
    try {
      const r = await lookup.run({ query: v });
      setW(r.data);
    } catch {
      setW(null);
    }
  };

  const formErr = {
    issue: !form.issue ? 'Pick the faulty part' : '',
    description: !form.description.trim() ? 'Describe the problem' : '',
    phone: form.phone && !isValidPhone(form.phone) ? 'Enter a 10-digit mobile number' : '',
  };
  const err = (k) => (touched && formErr[k]) || undefined;

  const submitClaim = async () => {
    setTouched(true);
    if (Object.values(formErr).some(Boolean)) return;
    try {
      const r = await claim.run({ warranty_id: w.id, issue: form.issue, description: form.description, contact_phone: form.phone });
      setCreated(r.data);
      setTouched(false);
      setForm((x) => ({ ...x, issue: '', description: '' }));
      setW((x) => ({ ...x, claims: [r.data, ...(x.claims || [])] }));
    } catch {
      /* shown below */
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Warranty and claims" subtitle="Check what is covered and raise a claim." />
      <SectionCard title="Find your warranty">
        <form onSubmit={find} className="flex max-w-xl items-start gap-2">
          <div className="flex-1"><Input
            aria-label="Serial or order number"
            placeholder="Serial number or order number"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />} /></div>
          <Button type="submit" loading={lookup.loading}>Check</Button>
        </form>
        <p className="text-xs opacity-70">Anyone can check coverage by serial number. To look up by order number, raise a claim or see your claims, sign in as the buyer.</p>
        {lookup.error ? <Alert variant="error">{lookup.error}</Alert> : null}
      </SectionCard>

      {!w && !lookup.error ? (
        <EmptyState title="Look up an item to see its coverage">Your order number is on the invoice and in My orders. The serial is on the item's label or box.</EmptyState>
      ) : null}

      {w ? (
        <div className="grid gap-5 lg:grid-cols-2">
          <SectionCard title="Coverage" actions={<StatusBadge status={w.status === 'active' ? 'active' : 'expired'} label={{ active: 'Active', expired: 'Expired', void: 'Void' }[w.status] || w.status} />}>
            <KeyValue
              items={[
                { label: 'Product', value: w.product },
                { label: 'Serial', value: `${w.serial_masked} (masked)` },
                w.order_id ? { label: 'Order', value: w.order_id } : null,
                { label: 'Starts', value: formatDate(w.starts_on) },
                { label: 'Ends', value: formatDate(w.ends_on) },
                { label: 'Policy', value: w.policy_version },
              ]}
            />
            <ul className="space-y-1.5 border-t border-[var(--app-border)] pt-3 text-sm">
              {w.coverage.map((c) => (
                <li key={c.part} className="flex items-start gap-2">
                  {c.covered ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success-600" aria-label="Covered" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-error-600" aria-label="Not covered" />}
                  <span className="flex-1">{c.part}</span>
                  <span className="text-xs opacity-70">{c.covered ? `until ${formatDate(c.until)}` : 'not covered'}</span>
                </li>
              ))}
            </ul>
          </SectionCard>

          <div className="space-y-5">
            <SectionCard title="Raise a claim">
              {w.claims === null ? (
                <Alert variant="info" title={sf.session ? 'This item is not on your account' : 'Sign in to raise a claim'}>
                  {sf.session ? 'Claims can be raised by the buyer, or by calling the store.' : 'Claims and their status are visible to the buyer only.'}
                  {!sf.session ? <div className="mt-2"><Button size="sm" variant="outline" onClick={() => navigate(`/account/login?next=${encodeURIComponent('/warranty')}`)}>Sign in</Button></div> : null}
                </Alert>
              ) : w.status !== 'active' ? (
                <Alert variant="warning">This warranty has expired. You can still book a paid repair.</Alert>
              ) : (
                <div className="space-y-3">
                  {created ? <Alert variant="success" title={`Claim ${created.id} submitted`}>The seller reviews claims within 2 working days and will arrange pickup if needed.</Alert> : null}
                  <Select label="Faulty part" required options={PARTS} value={form.issue} onChange={(v) => setForm((x) => ({ ...x, issue: v }))} error={err('issue')} closeOnSelect placeholder="Choose a part" />
                  <Textarea label="What is happening?" required rows={3} maxLength={800} showCount value={form.description} onChange={(e) => setForm((x) => ({ ...x, description: e.target.value }))} error={err('description')} />
                  <p className="text-xs opacity-70">Our technician inspects the item when it is collected; you can show photos or a video of the fault then.</p>
                  <Input label="Contact number" type="tel" value={form.phone} onChange={(e) => setForm((x) => ({ ...x, phone: e.target.value }))} error={err('phone')} placeholder="98765 43210" />
                  {claim.error ? <Alert variant="error">{claim.error}</Alert> : null}
                  <Button loading={claim.loading} onClick={submitClaim}>Submit claim</Button>
                </div>
              )}
            </SectionCard>

            {w.claims ? <SectionCard title="Claims">
              {!w.claims.length ? <p className="text-sm opacity-70">No claims on this item.</p> : (
                <ul className="divide-y divide-[var(--app-border)] text-sm">
                  {w.claims.map((c) => (
                    <li key={c.id} className="space-y-1 py-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-semibold">{c.id} · {c.issue}</span>
                        <StatusBadge status={c.status} size="xs" />
                      </div>
                      <div className="opacity-80">{c.description}</div>
                      <div className="text-xs opacity-60">Raised {formatDate(c.created_at)}{c.attachments.length ? ` · ${c.attachments.length} attachment(s)` : ''}</div>
                      {c.resolution ? <div className="text-xs"><Badge size="xs" variant="outline">Outcome</Badge> {c.resolution}</div> : null}
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
